import { useRef, useState } from 'react';
import { parseCsvText, readFileAsText, validateHeaders } from 'utils/importCsv';

/**
 * Manages CSV file input state and handlers for the import modal.
 *
 * @returns {Object} File state values, the hidden input ref, and action handlers
 * @returns {React.RefObject} returns.fileInputRef   - Ref to attach to the hidden <input type="file">
 * @returns {File|null}       returns.selectedFile   - Currently selected File object
 * @returns {string}          returns.fileName       - Display name of the selected file
 * @returns {string}          returns.fileError      - File-level validation error message
 * @returns {Array}           returns.parsedData     - Parsed CSV rows as record objects
 * @returns {string[]}        returns.parseErrors    - CSV header validation error messages
 * @returns {boolean}         returns.isParsingCsv   - True while the CSV is being read/parsed
 * @returns {Function}        returns.setFileError   - Setter for fileError (used by confirmation logic in modal)
 * @returns {Function}        returns.handleFileInputChange - onChange handler for the hidden file input
 * @returns {Function}        returns.handleAttachFile      - Triggers the hidden file input click
 * @returns {Function}        returns.handleRemoveFile      - Clears selected file and all derived state
 */
const useCsvFileInput = () => {
  const fileInputRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileError, setFileError] = useState('');
  const [parsedData, setParsedData] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);
  const [isParsingCsv, setIsParsingCsv] = useState(false);

  /**
   * Handles file input change event.
   * Validates file type (.csv) and size (max 5 MB), then reads and parses
   * the CSV content automatically on selection.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event - The file input change event
   */
  const handleFileInputChange = async (event) => {
    const file = event.target.files[0];

    // Reset all previous state before processing the new selection
    setFileError('');
    setParsedData([]);
    setParseErrors([]);
    setSelectedFile(null);
    setFileName('');

    if (!file) return;

    // Validate file type (must be .csv)
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setFileError('Please select a CSV file');
      event.target.value = '';
      return;
    }

    // Validate file size (max 50 MB)
    if (file.size > 50 * 1024 * 1024) {
      setFileError('File size must be less than 50MB');
      event.target.value = '';
      return;
    }

    setSelectedFile(file);
    setFileName(file.name);
    setIsParsingCsv(true);

    try {
      const csvText = await readFileAsText(file);
      const rows = parseCsvText(csvText);

      if (rows.length === 0) {
        setFileError('CSV file is empty');
        setIsParsingCsv(false);
        return;
      }

      // First row is the header
      const headers = rows[0];
      const headerErrors = validateHeaders(headers);

      if (headerErrors.length > 0) {
        setParseErrors(headerErrors);
        setIsParsingCsv(false);
        return;
      }

      // Map remaining rows to keyed record objects; rowNumber is 1-indexed from the original file
      const dataRows = rows.slice(1).map((row, index) => {
        const record = {};
        headers.forEach((header, i) => {
          record[header] = row[i] || '';
        });
        record.rowNumber = index + 2; // +2: 0-based index + skipped header row
        return record;
      });

      setParsedData(dataRows);
      setIsParsingCsv(false);
    } catch (error) {
      setFileError(`Failed to parse CSV: ${error.message}`);
      setIsParsingCsv(false);
    }

    // Reset input value so the same file can trigger onChange again if re-selected
    event.target.value = '';
  };

  const handleAttachFile = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileName('');
    setFileError('');
    setParsedData([]);
    setParseErrors([]);
    setIsParsingCsv(false);
  };

  return {
    fileInputRef,
    selectedFile,
    fileName,
    fileError,
    parsedData,
    parseErrors,
    isParsingCsv,
    setFileError,
    handleFileInputChange,
    handleAttachFile,
    handleRemoveFile,
  };
};

export default useCsvFileInput;
